import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Breadcrumb, Card, Button, Table, Tag, Modal, Space, message, Form, Input, Select } from 'antd';
import * as Icon from 'react-feather';
import { Link } from 'react-router-dom';
import {
  badgeActionStart,
  badgeActionError,
  getAllBadgesAction,
  createBadgeAction,
  updateBadgeAction,
  deleteBadgeAction,
} from '../../core/data/redux/modules/badge/actions';
import { getAllBadges, createBadge, updateBadge, deleteBadge } from '../../services/adminServices/badge';

const BADGE_TYPES = [
  { value: 'POLL_COUNT_DRIVEN', label: 'Poll Count Driven' },
  { value: 'VOTE_RECEIVED_COUNT_DRIVEN', label: 'Vote Received Count Driven' },
  { value: 'VOTE_GIVEN_COUNT_DRIVEN', label: 'Vote Given Count Driven' },
  { value: 'POLL_STREAK_COUNT_DRIVEN', label: 'Poll Streak Count Driven' },
  { value: 'VOTE_STREAK_COUNT_DRIVEN', label: 'Vote Streak Count Driven' },
];

const formatBadgeType = (type: string) => {
  const badgeType = BADGE_TYPES.find(bt => bt.value === type);
  return badgeType ? badgeType.label : type;
};

const ListBadges = () => {
  const dispatch = useDispatch();
  const { badges, loading } = useSelector((state: any) => state.badge || { badges: [], loading: false });
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingBadge, setEditingBadge] = useState<any>(null);
  const [formValues, setFormValues] = useState<any>({ name: '', type: '', is_active: true });
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    fetchBadges();
    // eslint-disable-next-line
  }, []);

  const fetchBadges = async () => {
    dispatch(badgeActionStart());
    try {
      const res = await getAllBadges();
      if (res.error) throw new Error(res.message || 'Error fetching badges');
      const normalized = Array.isArray(res.data) ? res.data : Array.isArray(res.data?.data) ? res.data.data : [];
      dispatch(getAllBadgesAction(normalized));
    } catch (err: any) {
      dispatch(badgeActionError(err.message));
      message.error(err.message);
    }
  };

  const handleAdd = () => {
    setEditingBadge(null);
    setFormValues({ name: '', type: '', is_active: true });
    setIsModalVisible(true);
  };

  const handleEdit = (badge: any) => {
    setEditingBadge(badge);
    setFormValues({ name: badge.name, type: badge.type || '', is_active: badge.is_active });
    setIsModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      await deleteBadge(id);
      dispatch(deleteBadgeAction(id));
      message.success('Badge deleted');
      fetchBadges();
    } catch (err: any) {
      message.error(err.message);
    } finally {
      setDeletingId(null);
    }
  };

  const handleModalOk = async () => {
    try {
      // Validate required fields when creating
      if (!editingBadge) {
        if (!formValues.type) {
          message.error('Badge type is required');
          return;
        }
        if (!formValues.name) {
          message.error('Badge name is required');
          return;
        }
      }

      if (editingBadge) {
        const res = await updateBadge(editingBadge.id, formValues);
        if (res.error) throw new Error(res.message);
        const updatedBadge = res.data?.data || res.data;
        dispatch(updateBadgeAction(updatedBadge));
        message.success('Badge updated');
      } else {
        const res = await createBadge(formValues);
        if (res.error) throw new Error(res.message);
        const createdBadge = res.data?.data || res.data;
        dispatch(createBadgeAction(createdBadge));
        message.success('Badge created');
      }
      setIsModalVisible(false);
      fetchBadges();
    } catch (err: any) {
      message.error(err.message);
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => formatBadgeType(type),
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: any) => (
        <Space>
          <Link to={`/admin/badge/badge-detail/${record.id}`}>
            <Button icon={<Icon.Eye size={16} />} size="small" />
          </Link>
          <Button icon={<Icon.Edit size={16} />} size="small" onClick={() => handleEdit(record)} />
          <Button icon={<Icon.Trash2 size={16} />} size="small" danger loading={deletingId === record.id} onClick={() => handleDelete(record.id)} />
        </Space>
      ),
    },
  ];

  return (
    <div className="page-wrapper page-settings" style={{ paddingTop: 0 }}>
      <div className="content">
        <Breadcrumb style={{ marginBottom: 20 }}>
          <Breadcrumb.Item>
            <Link to="/admin/badge/list-badges">Badges</Link>
          </Breadcrumb.Item>
        </Breadcrumb>
        <Card>
          <Space style={{ marginBottom: 16 }}>
            <Button icon={<Icon.Plus size={16} />} type="primary" onClick={handleAdd}>
              Add Badge
            </Button>
          </Space>
          <Table
            columns={columns}
            dataSource={badges}
            loading={loading}
            rowKey="id"
            pagination={{ pageSize: 10 }}
          />
        </Card>
        <Modal
          title={editingBadge ? 'Edit Badge' : 'Add Badge'}
          open={isModalVisible}
          onOk={handleModalOk}
          onCancel={() => setIsModalVisible(false)}
          okText={editingBadge ? 'Update' : 'Create'}
        >
          <Form layout="vertical">
            <Form.Item label="Name" required>
              <Input
                value={formValues.name}
                onChange={e => setFormValues({ ...formValues, name: e.target.value })}
                placeholder="Enter badge name"
              />
            </Form.Item>
            <Form.Item label="Type" required={!editingBadge}>
              <Select
                value={formValues.type}
                onChange={value => setFormValues({ ...formValues, type: value })}
                placeholder="Select badge type"
                style={{ width: '100%' }}
                disabled={!!editingBadge}
              >
                {BADGE_TYPES.map(badgeType => (
                  <Select.Option key={badgeType.value} value={badgeType.value}>
                    {badgeType.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item label="Status">
              <Select
                value={formValues.is_active}
                onChange={value => setFormValues({ ...formValues, is_active: value })}
                style={{ width: '100%' }}
              >
                <Select.Option value={true}>Active</Select.Option>
                <Select.Option value={false}>Inactive</Select.Option>
              </Select>
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </div>
  );
};

export default ListBadges; 